/**
 * Borrower (customer) self-service portal client. Strictly backend-backed:
 * credentials, sign-in and the read-only overview all go through MongoDB via
 * `/api/customers/*`. There is no offline/local fallback.
 *
 * Credentials are auto-provisioned when a finance facility is created:
 *   email    = first 4 letters of name + last 4 digits of phone @ <company>.com
 *   password = the customer's phone number
 */
import {
  customerApi,
  ApiError,
  setCustomerToken,
  clearCustomerToken,
  getCustomerToken,
  hasCustomerToken,
  type CustomerCompany,
  type CustomerAccountPublic,
} from '../api';
import {
  aggregateCustomersFrom,
  buildCustomerScheduleFrom,
  getFacilityDueSummary,
  type UnifiedCustomer,
  type CustomerInstallmentRow,
  type AdminEmiPayment,
  type FacilityDueSummary,
} from './workspaceStore';

const SESSION = {
  account: 'ef_customer_account',
  company: 'ef_customer_company',
} as const;

export type ProvisionResult = { email: string; password: string; created: boolean };

export type CustomerPortalData = {
  account: CustomerAccountPublic;
  company: CustomerCompany | null;
  customer: UnifiedCustomer | null;
  schedule: CustomerInstallmentRow[];
  payments: AdminEmiPayment[];
  facilitySummaries: FacilityDueSummary[];
};

function digits(s: unknown): string {
  return String(s ?? '').replace(/\D/g, '');
}

// ---- Provisioning (called after a finance facility is saved) ----

/** Provision (or link) a borrower login for a finance facility, in MongoDB. */
export async function provisionCustomerForFinance(input: {
  name: string;
  phone: string;
  recordId?: string;
}): Promise<ProvisionResult | null> {
  const phone = String(input.phone || '').trim();
  const phoneDigits = digits(phone);
  if (!phoneDigits || phoneDigits.length < 4) return null;
  const res = await customerApi.provision({ name: input.name, phone: phoneDigits, recordId: input.recordId });
  return { email: res.email, password: res.password, created: res.created };
}

// ---- Session ----

function setSession(account: CustomerAccountPublic, company: CustomerCompany | null) {
  try {
    localStorage.setItem(SESSION.account, JSON.stringify(account));
    localStorage.setItem(SESSION.company, JSON.stringify(company));
  } catch {
    /* ignore */
  }
}

export function isCustomerLoggedIn(): boolean {
  return hasCustomerToken();
}

export function clearCustomerSession(): void {
  clearCustomerToken();
  Object.values(SESSION).forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  });
}

export type CustomerLoginResult = { ok: true } | { ok: false; error: string };

/** Borrower sign-in (backend only). */
export async function loginCustomer(email: string, password: string): Promise<CustomerLoginResult> {
  const e = email.trim().toLowerCase();
  const p = String(password || '');
  if (!e || !p) return { ok: false, error: 'Enter your email and password.' };

  try {
    const res = await customerApi.login({ email: e, password: p });
    setCustomerToken(res.token);
    setSession(res.account, res.company);
    return { ok: true };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 400)) {
      return { ok: false, error: 'Invalid email or password.' };
    }
    return { ok: false, error: 'Could not reach the server. Please try again.' };
  }
}

// ---- Read-only overview ----

function pickCustomerByPhone(customers: UnifiedCustomer[], phone: string): UnifiedCustomer | null {
  if (!customers.length) return null;
  const norm = digits(phone);
  return (
    customers.find((c) => digits(c.phone) === norm) ||
    customers.find((c) => c.id === `ph:${phone}`) ||
    customers[0] ||
    null
  );
}

/** Fetch the borrower's read-only EMI overview from the backend. */
export async function fetchCustomerPortalData(): Promise<CustomerPortalData> {
  const token = getCustomerToken();
  if (!token) throw new Error('Your session has expired. Please sign in again.');
  const ov = await customerApi.overview(token);
  const payments = (ov.payments as unknown as AdminEmiPayment[]) || [];
  const customers = aggregateCustomersFrom(ov.finance as never, ov.handloans as never, payments);
  const customer = pickCustomerByPhone(customers, ov.account.phone);
  const facilitySummaries = customer
    ? customer.facilities.map((f) => getFacilityDueSummary(f, payments))
    : [];
  return {
    account: ov.account,
    company: ov.company,
    customer,
    schedule: customer ? buildCustomerScheduleFrom(customer, payments) : [],
    payments,
    facilitySummaries,
  };
}
